import { useCallback, useEffect, useRef, useState } from 'react';
import getAllUsers from './getAllUsers';
import { User } from './useGetAllUsersPaginated';

interface UseAllUsersForFilterParams {
  enabled: boolean;
  totalCount?: number;
}

interface UsersCache {
  totalCount: number;
  users: User[];
}

/**
 * Loads all users when filters are active so they can be filtered across pages.
 * Caches the result so subsequent searches do not refetch.
 * @param params.enabled - Whether the full list should be fetched
 * @param params.totalCount - Total users reported by pagination
 */
const useAllUsersForFilter = ({ enabled, totalCount }: UseAllUsersForFilterParams) => {
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const [error, setError] = useState(false);
  const cacheRef = useRef<UsersCache | null>(null);

  /**
   * Clears the cached users list so the next search refetches
   */
  const invalidateCache = useCallback(() => {
    cacheRef.current = null;
    setAllUsers([]);
  }, []);

  useEffect(() => {
    let isCancelled = false;

    /**
     * Fetches the complete users list using cache when available
     */
    async function fetchAllUsers() {
      if (!enabled || !totalCount) {
        setIsLoadingAll(false);
        setError(false);
        return;
      }

      if (cacheRef.current?.totalCount === totalCount) {
        setAllUsers(cacheRef.current.users);
        setIsLoadingAll(false);
        setError(false);
        return;
      }

      setIsLoadingAll(true);
      setError(false);

      try {
        const users = await getAllUsers(totalCount);
        if (!isCancelled) {
          cacheRef.current = { totalCount, users };
          setAllUsers(users);
        }
      } catch {
        if (!isCancelled) {
          setAllUsers([]);
          setError(true);
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingAll(false);
        }
      }
    }

    fetchAllUsers();

    return () => {
      isCancelled = true;
    };
  }, [enabled, totalCount]);

  return { allUsers, isLoadingAll, error, invalidateCache };
};

export default useAllUsersForFilter;
